/**
 * 量化回测系统 - 修复版
 * 修复布局问题和按钮点击问题
 */
import React, { useState, useEffect, useCallback, useMemo, memo } from 'react';
import { 
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import { 
  TrendingUp, TrendingDown, Activity, DollarSign, 
  Settings, Play, RotateCcw, Filter, Download, Bell, AlertCircle, CheckCircle
} from 'lucide-react';
import './App.css';

// ==================== API 服务 ====================
const API_BASE_URL = 'http://localhost:8000';

async function runBacktest(config) {
  try {
    const backendConfig = {
      symbol: config.symbol,
      timeframe: config.timeframe,
      start_time: config.startTime,
      end_time: config.endTime,
      initial_capital: config.initialCapital || 1000,
      leverage: config.leverage || 20,
      boll_period: config.bollPeriod || 20,
      boll_dev: config.bollDev || 1.5,
      ema_period: config.emaPeriod || 10,
      max_daily_trades: config.maxDailyTrades || 50
    };

    const response = await fetch(`${API_BASE_URL}/api/backtest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(backendConfig),
      timeout: 60000
    });

    if (!response.ok) throw new Error(`HTTP ${response.status}`);
    
    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    console.error('回测失败:', error);
    return { success: false, error: error.message };
  }
}

// ==================== 模拟数据 ====================
const generateMockData = (() => {
  let cache = null;
  return () => {
    if (cache) return cache;
    const data = [];
    let value = 1000;
    for (let i = 0; i < 100; i++) {
      value += (Math.random() - 0.5) * 50;
      data.push({
        time: `${Math.floor(i / 4)}:${(i % 4) * 15}`.padStart(5, '0'),
        value: parseFloat(value.toFixed(2))
      });
    }
    cache = data;
    return data;
  };
})();

// ==================== 子组件 ====================

const StatCard = memo(({ icon: Icon, label, value, type = 'neutral' }) => (
  <div className={`stat-card ${type}`}>
    <div className="stat-icon">
      <Icon size={24} />
    </div>
    <div className="stat-content">
      <span className="stat-label">{label}</span>
      <span className="stat-value">{value}</span>
    </div>
  </div>
));

// ==================== 主组件 ====================

function App() {
  const [activeTab, setActiveTab] = useState('chart');
  const [isRunning, setIsRunning] = useState(false);
  const [data, setData] = useState(generateMockData);
  const [selectedSymbol, setSelectedSymbol] = useState('XRP/USDT:USDT');
  const [timeRange, setTimeRange] = useState('15m');
  const [backtestResult, setBacktestResult] = useState(null);
  const [error, setError] = useState(null);

  const stats = useMemo(() => ({
    totalReturn: backtestResult?.summary?.total_return 
      ? `${backtestResult.summary.total_return > 0 ? '+' : ''}${backtestResult.summary.total_return.toFixed(2)}%`
      : '+8.35%',
    sharpeRatio: backtestResult?.summary?.sharpe_ratio?.toFixed(2) || '1.45',
    maxDrawdown: backtestResult?.summary?.max_drawdown 
      ? `${backtestResult.summary.max_drawdown.toFixed(1)}%`
      : '-12.3%',
    winRate: backtestResult?.summary?.win_rate?.toFixed(1) || '62.5',
    totalTrades: backtestResult?.summary?.total_trades || 24
  }), [backtestResult]);

  const handleRunBacktest = useCallback(async () => {
    setIsRunning(true);
    setError(null);

    try {
      const result = await runBacktest({
        symbol: selectedSymbol,
        timeframe: timeRange,
        startTime: '2025-01-01T00:00:00Z',
        endTime: '2025-01-31T23:59:59Z'
      });

      if (result.success) {
        setBacktestResult(result.data);
        if (result.data.equity_curve) {
          const chartData = result.data.equity_curve.map(item => ({
            time: new Date(item.date).toLocaleString('zh-CN', { 
              month: '2-digit', day: '2-digit',
              hour: '2-digit', minute: '2-digit' 
            }),
            value: item.equity
          }));
          setData(chartData);
        }
      } else {
        setData(generateMockData());
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
      setData(generateMockData());
    } finally {
      setIsRunning(false);
    }
  }, [selectedSymbol, timeRange]);

  const navItems = useMemo(() => [
    { id: 'chart', icon: TrendingUp, label: 'K 线图表' },
    { id: 'backtest', icon: Activity, label: '回测分析' },
    { id: 'trades', icon: DollarSign, label: '交易记录' },
    { id: 'settings', icon: Settings, label: '策略配置' }
  ], []);

  return (
    <div className="app-container">
      {/* Header */}
      <header className="header">
        <div className="logo">
          <Activity className="logo-icon" />
          <h1>QuantBacktest Pro</h1>
          <span className="version-tag">v2.0</span>
        </div>
        <div className="header-actions">
          {error && (
            <div className="error-toast">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}
          <button className="icon-btn"><Bell size={20} /></button>
          <div className="user-avatar">U</div>
        </div>
      </header>

      {/* Main Content */}
      <div className="main-content">
        {/* Sidebar */}
        <aside className="sidebar">
          <nav className="nav-menu">
            {navItems.map(item => (
              <button 
                key={item.id}
                className={`nav-item ${activeTab === item.id ? 'active' : ''}`}
                onClick={() => setActiveTab(item.id)}
              >
                <item.icon size={18} />
                <span>{item.label}</span>
              </button>
            ))}
          </nav>
        </aside>

        {/* Main Panel */}
        <main className="main-panel">
          {/* Control Bar */}
          <div className="control-bar">
            <div className="control-group">
              <label>交易对</label>
              <select 
                value={selectedSymbol} 
                onChange={(e) => setSelectedSymbol(e.target.value)}
                className="select-input"
                disabled={isRunning}
              >
                <option value="XRP/USDT:USDT">XRP/USDT</option>
                <option value="BTC/USDT:USDT">BTC/USDT</option>
                <option value="ETH/USDT:USDT">ETH/USDT</option>
              </select>
            </div>
            <div className="control-group">
              <label>周期</label>
              <select 
                value={timeRange} 
                onChange={(e) => setTimeRange(e.target.value)}
                className="select-input"
                disabled={isRunning}
              >
                <option value="15m">15 分钟</option>
                <option value="1h">1 小时</option>
                <option value="4h">4 小时</option>
              </select>
            </div>
            <button 
              className={`btn-primary ${isRunning ? 'loading' : ''}`}
              onClick={handleRunBacktest}
              disabled={isRunning}
            >
              {isRunning ? (
                <><RotateCcw className="spin" size={16} /> 运行中...</>
              ) : (
                <><Play size={16} /> 运行回测</>
              )}
            </button>
          </div>

          {/* Stats Cards */}
          <div className="stats-grid">
            <StatCard 
              icon={TrendingUp} 
              label="总收益率" 
              value={stats.totalReturn}
              type={stats.totalReturn.startsWith('+') ? 'positive' : 'negative'}
            />
            <StatCard icon={Activity} label="夏普比率" value={stats.sharpeRatio} />
            <StatCard icon={TrendingDown} label="最大回撤" value={stats.maxDrawdown} type="negative" />
            <StatCard icon={DollarSign} label="胜率" value={`${stats.winRate}%`} />
          </div>

          {/* Chart Section */}
          <div className="chart-section">
            <div className="chart-header">
              <h3>资金曲线</h3>
              <div className="chart-controls">
                <button className="icon-btn"><Filter size={16} /></button>
                <button className="icon-btn"><Download size={16} /></button>
              </div>
            </div>
            <div className="chart-container">
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={data}>
                  <defs>
                    <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                      <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                  <XAxis dataKey="time" stroke="#64748b" fontSize={12} />
                  <YAxis stroke="#64748b" fontSize={12} />
                  <Tooltip contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', color: '#fff' }} />
                  <Area type="monotone" dataKey="value" stroke="#6366f1" strokeWidth={2} fill="url(#colorValue)" />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Backtest Result */}
          {backtestResult && (
            <div className="backtest-info">
              <div className="info-card">
                <CheckCircle size={16} className="success-icon" />
                <span>回测完成</span>
                <span className="info-detail">
                  {backtestResult.data_points} 条数据 | 
                  交易次数：{backtestResult.summary?.total_trades || 0}
                </span>
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

export default App;